import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'

// Global flag to track if animation has played for this page load
let hasAnimatedOnPageLoad = false

export interface AnimatedSparkleIconProps {
  width?: number | string
  height?: number | string
  className?: string
  animate?: boolean
  style?: React.CSSProperties
  disabled?: boolean
  triggerAnimation?: boolean
  animationType?: 'default' | 'thinking'
  'data-testid'?: string
}

const spinAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(180deg);
  }
`

const thinkingAnimation = keyframes`
  0% {
    transform: rotate(180deg);
  }
  50% {
    transform: rotate(90deg);
  }
  100% {
    transform: rotate(0deg);
  }
`

const AnimatedSvg = styled.svg<{ animate: boolean; animationType: 'default' | 'thinking' }>(
  ({ animate, animationType }) => ({
    transformOrigin: '50% 50%',
    ...(animate &&
      animationType === 'default' && {
        animation: `${spinAnimation} 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`,
      }),
    ...(animate &&
      animationType === 'thinking' && {
        animation: `${thinkingAnimation} 600ms ease-in-out infinite`,
      }),
  })
)

export const AnimatedSparkleIcon: React.FC<AnimatedSparkleIconProps> = ({
  width = 53,
  height = 53,
  className,
  animate = true,
  style,
  disabled = false,
  triggerAnimation = false,
  animationType = 'default',
  'data-testid': dataTestId,
}) => {
  const [shouldAnimate, setShouldAnimate] = useState(false)

  useEffect(() => {
    if (animate && triggerAnimation) {
      if (animationType === 'thinking') {
        // For thinking animation, animate continuously while triggerAnimation is true
        setShouldAnimate(true)
      } else if (!hasAnimatedOnPageLoad) {
        // For default animation, only animate once per page load
        const timer = setTimeout(() => {
          setShouldAnimate(true)
          hasAnimatedOnPageLoad = true
        }, 50)
        return () => clearTimeout(timer)
      }
    } else if (animationType === 'thinking') {
      // Stop thinking animation when triggerAnimation is false
      setShouldAnimate(false)
    }
  }, [animate, triggerAnimation, animationType])
  const iconStyle = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    ...style,
  }

  return (
    <AnimatedSvg
      animate={shouldAnimate}
      animationType={animationType}
      className={className}
      width={iconStyle.width}
      height={iconStyle.height}
      viewBox="0 0 53 53"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={iconStyle}
      data-testid={dataTestId}
    >
      <path
        d="M26.25 50.3125C26.25 50.3125 24.5666 38.1784 19.4441 33.0559C14.3216 27.9334 2.1875 26.25 2.1875 26.25C2.1875 26.25 14.3216 24.5666 19.4441 19.4441C24.5666 14.3216 26.25 2.1875 26.25 2.1875C26.25 2.1875 27.9334 14.3216 33.0559 19.4441C38.1784 24.5666 50.3125 26.25 50.3125 26.25C50.3125 26.25 38.1784 27.9334 33.0559 33.0559C27.9334 38.1784 26.25 50.3125 26.25 50.3125Z"
        fill={disabled ? '#9CA3AF' : 'url(#paint0_linear_887_75562)'}
      />
      <defs>
        <linearGradient
          id="paint0_linear_887_75562"
          x1="56.7292"
          y1="-2.625"
          x2="-20.224"
          y2="17.6426"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.24" stopColor="#3098D1" />
          <stop offset="0.705" stopColor="#14CC95" />
        </linearGradient>
      </defs>
    </AnimatedSvg>
  )
}
